/**
 * Pure rules the transfers surfaces share (chip, toast, S2 panel). Everything
 * is derived from the `TransferJob` snapshot the main process emits — the
 * renderer never touches the filesystem: `.part` existence is already folded
 * into `job.recovery` (spec §7.1, the ONE eligibility rule).
 */
import type { TransferJob, TransferJobState } from '../../../types/azureStorageTransfers';
import { isLeaseError, LEASE_NEXT_STEP } from './transferErrorText';

const ACTIVE_STATES: ReadonlySet<TransferJobState> = new Set(['planning', 'queued', 'running', 'cancelling']);

export function isActiveTransferState(state: TransferJobState): boolean {
  return ACTIVE_STATES.has(state);
}

export function isTerminalTransferState(state: TransferJobState): boolean {
  return !ACTIVE_STATES.has(state);
}

export type ControlOffer = { shown: boolean; enabled: boolean; reason?: string };

/** Cancel: planning / queued / running accept it; `cancelling` shows it disabled so the row explains itself. */
export function canCancelTransfer(job: TransferJob): ControlOffer {
  if (job.state === 'cancelling') return { shown: true, enabled: false, reason: 'Cancel already requested' };
  if (isActiveTransferState(job.state)) return { shown: true, enabled: true };
  return { shown: false, enabled: false };
}

/**
 * Resume / Restart offers. Only a non-active snapshot can be recovered; among
 * those the `recovery` field is the single source: `'resume-plan'` → both,
 * `'restart-only'` → Restart alone, `'none'` → neither.
 */
export function recoveryOffersFor(job: TransferJob): { resume: boolean; restart: boolean } {
  if (isActiveTransferState(job.state)) return { resume: false, restart: false };
  if (job.recovery === 'resume-plan') return { resume: true, restart: true };
  if (job.recovery === 'restart-only') return { resume: false, restart: true };
  return { resume: false, restart: false };
}

/** Local destination path of a download, if the snapshot carries one. */
export function transferLocalPath(job: TransferJob): string | undefined {
  return job.destination.kind === 'local' ? job.destination.path : undefined;
}

const lastSegment = (raw: string): string => {
  const segments = raw.split(/[\\/]/).filter(Boolean);
  return segments[segments.length - 1] ?? raw;
};

function endpointName(endpoint: TransferJob['source']): string {
  if (endpoint.kind === 'local') return lastSegment(endpoint.path);
  const resource = endpoint.resource;
  if (resource.kind === 'blob') return lastSegment(resource.name);
  if (resource.kind === 'blobPrefix') return lastSegment(resource.prefix) || resource.container;
  if (resource.kind === 'container') return resource.container;
  if (resource.kind === 'share') return resource.share;
  if (resource.kind === 'directory') return lastSegment(resource.path) || resource.share;
  if (resource.kind === 'file') return lastSegment(resource.path);
  if (resource.kind === 'hnsPath') return lastSegment(resource.path) || resource.fileSystem; // S8: an `acl` job's root
  return '';
}

/** S7 §3.8: a delete job's destination is its anchor — shown whole: `logs/2026/`, `share/dir`. */
function anchorName(endpoint: TransferJob['destination']): string {
  if (endpoint.kind === 'local') return lastSegment(endpoint.path);
  const resource = endpoint.resource;
  if (resource.kind === 'blob') return resource.name;
  if (resource.kind === 'blobPrefix') return resource.prefix || resource.container;
  if (resource.kind === 'directory' || resource.kind === 'file') return resource.path ? `${resource.share}/${resource.path}` : resource.share;
  return endpointName(endpoint);
}

/**
 * The row name: the local file a download writes, otherwise what the job acts
 * on — an upload's first local source (review B minor: upload rows target a
 * container or prefix and were blank), a delete's anchor (S7 §3.8: the selected
 * ref, or the deepest place that holds every selected ref). A multi-selection
 * says how many more it carries.
 */
export function transferFileName(job: TransferJob): string {
  const count = job.sources?.length ?? 1;
  if (job.kind === 'delete') return count > 1 ? `${anchorName(job.destination)} (${count} selected)` : anchorName(job.destination);
  if (job.destination.kind === 'local' || job.destination.resource.kind === 'blob' || job.destination.resource.kind === 'file') return endpointName(job.destination);
  const name = endpointName(job.source);
  return count > 1 ? `${name} + ${count - 1} more` : name;
}

/** A collision's destination: a storage resource key (JSON tuple) shows as `container/name` / `share/path`; a local path as is. */
export function collisionDestinationLabel(destination: string): string {
  try {
    const parsed: unknown = JSON.parse(destination);
    if (Array.isArray(parsed) && (parsed[0] === 'blob' || parsed[0] === 'file') && typeof parsed[1] === 'string' && typeof parsed[2] === 'string') return `${parsed[1]}/${parsed[2]}`;
  } catch { /* a local path */ }
  return destination;
}

/**
 * S7 §3.4: the job-row line about remote state the job still owns (staged upload files) — "Removing staged upload
 * files…" while a cleanup pass runs, otherwise why files remain and how many; undefined when nothing remains.
 */
export function transferRemoteCleanupLine(job: TransferJob): string | undefined {
  const cleanup = job.remoteCleanup;
  if (!cleanup) return undefined;
  if (cleanup.active) return 'Removing staged upload files…';
  const counts = [
    cleanup.pending > 0 ? `${cleanup.pending} staged upload ${cleanup.pending === 1 ? 'file' : 'files'} to remove` : undefined,
    cleanup.unproven > 0 ? `${cleanup.unproven} left in place (could not be identified)` : undefined,
  ].filter(Boolean).join(', ');
  if (!counts) return undefined;
  return cleanup.reason ? `${counts}: ${cleanup.reason}` : counts;
}

export type TransferCleanupConnection = { exists: boolean; readOnly: boolean; connected: boolean; withinScope: boolean };

/**
 * What a finished job with staged files left offers besides Clear finished (S7 review D F4, review E D-2 — the rule
 * main's Forget applies): `forget` when its connection can never clean them (deleted, read-only, or re-scoped away from
 * the job's share); `connect` when the connection is only disconnected (connect, then Clear finished); nothing while
 * Clear finished can still try. Forget drops only the local record; the confirmation names every leftover.
 */
export function transferCleanupOffer(job: TransferJob, connection: TransferCleanupConnection): 'forget' | 'connect' | undefined {
  if (isActiveTransferState(job.state) || job.remoteCleanup?.active || (job.remoteCleanup?.pending ?? 0) === 0) return undefined;
  if (!connection.exists || connection.readOnly || !connection.withinScope) return 'forget';
  return connection.connected ? undefined : 'connect';
}

export function transferProgressPercent(job: TransferJob): number | undefined {
  if (job.state === 'completed') return 100;
  if (!job.bytesTotal || job.bytesTotal <= 0) return undefined;
  return Math.min(100, Math.max(0, Math.floor((job.bytesDone / job.bytesTotal) * 100)));
}

const STATE_LABELS: Record<TransferJobState, string> = {
  planning: 'Planning',
  queued: 'Queued',
  running: 'Downloading',
  cancelling: 'Cancelling',
  interrupted: 'Interrupted',
  completed: 'Completed',
  partial: 'Partly completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export function transferStateLabel(state: TransferJobState, kind?: TransferJob['kind']): string {
  if (state !== 'running' || !kind) return STATE_LABELS[state];
  return kind === 'upload' ? 'Uploading' : kind === 'delete' ? 'Deleting' : kind === 'copy' ? 'Copying' : kind === 'export' ? 'Exporting' : kind === 'acl' ? 'Applying access control' : STATE_LABELS[state];
}

/**
 * The row's error line. An `acl` job's summary already carries its committed root failure and its listed failures, so
 * the line appears only for a failed job with nothing committed (a guard refusal: read-only, disconnected — S8).
 */
export function transferRowFailure(job: TransferJob, lastErrorMessage?: string): string | undefined {
  if (job.state !== 'failed' && job.state !== 'partial') return undefined;
  if (job.kind === 'acl') return job.state === 'failed' && !job.acl?.traversal.failure ? (lastErrorMessage ?? job.errors[0]?.message) : undefined;
  // A16: a lease refusal leads with what to do; the service's own sentence stays in the item's details.
  const first = job.errors[0];
  if (first && isLeaseError(first)) return LEASE_NEXT_STEP;
  return lastErrorMessage ?? first?.message;
}


export function transferExportCompletionNote(job: TransferJob): string | undefined {
  if (job.kind !== 'export' || job.state !== 'completed' || !job.export || job.export.format === 'json') return undefined;
  const dropped = job.exportStats?.droppedValues ?? 0;
  return `${dropped} value${dropped === 1 ? '' : 's'} outside the fixed columns ${dropped === 1 ? 'was' : 'were'} dropped.`;
}

export { aggregateTransfers, type TransfersAggregate } from './transferAggregate';

export function formatTransferBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1; }
  return `${value < 10 ? value.toFixed(1) : Math.round(value).toString()} ${units[unit]}`;
}
